/**
 * The pure view-model behind the stock-market desk: a member's bankroll as a
 * quote (last / change / volume / high-low / drawdown), a sparkline off their
 * balance series, and the two audit tapes — settled rounds as trades, raw
 * ledger rows as time & sales. No React, no fetching.
 */
import {
  deriveStats,
  EMPTY_STATS,
  type BalanceSeries,
  type GameType,
  type CasinoLeaderboardEntry,
  type CasinoMemberView,
  type LedgerTapeEntry,
  type MemberStatsView,
  type RoundEntry,
} from '@tv-casino/shared';
import { formatSignedChips } from '../lib/format.js';
import {
  GAME_GLYPHS,
  GAME_LABELS,
  memberHistoryModel,
  type MemberHistoryVM,
} from './memberHistoryModel.js';

export interface SparkPoint {
  x: number;
  y: number;
}

/** What coloured a stretch of the sparkline: the game played, or the house. */
export type SparkTone = GameType | 'house';

export interface SparkRun {
  tone: SparkTone;
  points: SparkPoint[];
}

export interface SparkLegendItem {
  tone: GameType;
  label: string;
}

/**
 * A callout on the sparkline: the night's high and low, or chips the house
 * put in or took out (rebuy, grant, revoke) rather than a hand.
 */
export interface SparkMark {
  kind: 'high' | 'low' | 'chipsIn' | 'chipsOut';
  x: number;
  y: number;
  /** Printed on the chart: the balance for high/low, the signed move otherwise. */
  label: string;
  /** Longer read for tooltips and screen readers. */
  title: string;
}

export interface MarketMetric {
  label: string;
  value: string;
  /** Green/red when the figure itself is signed; otherwise plain. */
  tone: 'win' | 'loss' | 'even' | null;
}

/**
 * One exciting fact about the night, for the quote card's highlight strip —
 * the story a passer-by reads instead of four grey OHLC dashes.
 */
export interface MarketHighlight {
  key: string;
  /** The small print: "Best hand", "On a run", "Home table". */
  label: string;
  /** The big print: "+850", "4W", the game's name. */
  value: string;
  /** The game's glyph, when the highlight names a table. */
  game: GameType | null;
  tone: 'win' | 'loss' | 'even';
}

/** How many highlights a card shows — the best three facts win. */
const MAX_HIGHLIGHTS = 3;

/**
 * The night's story, most exciting first: the best hand, the run they are
 * on, the table they made home, the pot that towered. Every pick is earned —
 * a member with none yet shows no strip at all rather than a row of dashes.
 */
export function highlightsOf(
  totals: MemberStatsView,
  favoriteGame: GameType | null = null,
): MarketHighlight[] {
  const out: MarketHighlight[] = [];
  if (totals.bestRound > 0) {
    out.push({
      key: 'bestRound',
      label: 'Best hand',
      value: `+${chips(totals.bestRound)}`,
      game: null,
      tone: 'win',
    });
  }
  if (totals.winStreak >= 2) {
    out.push({
      key: 'streak',
      label: 'On a run',
      value: `${totals.winStreak}W`,
      game: null,
      tone: 'win',
    });
  } else if (totals.lossStreak >= 3) {
    out.push({
      key: 'streak',
      label: 'Cold spell',
      value: `${totals.lossStreak}L`,
      game: null,
      tone: 'loss',
    });
  }
  if (favoriteGame !== null && totals.rounds > 0) {
    out.push({
      key: 'favorite',
      label: 'Home table',
      value: GAME_LABELS[favoriteGame],
      game: favoriteGame,
      tone: 'even',
    });
  }
  if (totals.biggestPot > 0) {
    out.push({
      key: 'pot',
      label: 'Biggest pot',
      value: chips(totals.biggestPot),
      game: null,
      tone: 'win',
    });
  }
  return out.slice(0, MAX_HIGHLIGHTS);
}

export interface LedgerLineVM {
  key: string;
  whenLabel: string;
  kindLabel: string;
  /** Arrow for money leaving or returning, a plus for house moves. */
  icon: string;
  amountLabel: string;
  balanceLabel: string;
  tone: 'win' | 'loss' | 'even';
  /** |amount| against the largest move on the page, 0..1. */
  barFrac: number;
}

/**
 * The movements of one hand read together — the bet, then what came back —
 * or a lone house move (buy-in, rebuy, grant) standing on its own.
 */
export interface LedgerGroupVM {
  key: string;
  /** The game when the round is on the loaded trades page, else "Round". */
  title: string;
  glyph: string;
  whenLabel: string;
  netLabel: string;
  tone: 'win' | 'loss' | 'even';
  /** The balance once the group's last movement landed. */
  balanceLabel: string;
  /** Oldest first. */
  lines: LedgerLineVM[];
  house: boolean;
}

export interface MemberMarketVM {
  /** False while the membership card has not landed. */
  ready: boolean;
  /** Ticker-style symbol: first letters of the name + CHIP. */
  symbol: string;
  name: string;
  lastLabel: string;
  changeLabel: string;
  changePctLabel: string;
  changeTone: 'win' | 'loss' | 'even';
  highLabel: string;
  lowLabel: string;
  volumeLabel: string;
  /** Peak-to-trough drawdown as a negative percent, or "—" with no path. */
  drawdownLabel: string;
  metrics: MarketMetric[];
  /** The night's story: up to three earned highlights, most exciting first. */
  highlights: MarketHighlight[];
  spark: SparkPoint[];
  /** The line split into one run per game stretch, for colouring. */
  sparkRuns: SparkRun[];
  /** Keys for the games on the line; empty when only one game was played. */
  sparkLegend: SparkLegendItem[];
  /** High, low and house chip moves pinned to the line. */
  sparkMarks: SparkMark[];
  /** One settled movement: a mid-dot rather than a line. */
  sparkSingle: boolean;
  /**
   * Where the window opened, as the line's own 0–1 height — the water level
   * the fill reads against. Null when the line is flat or has no span.
   */
  sparkBaseline: number | null;
  trades: MemberHistoryVM;
  ledgerGroups: LedgerGroupVM[];
  ledgerHasMore: boolean;
  /** Shown instead of the desk before the first chip movement. */
  emptyLine: string | null;
}

export const KIND_LABELS: Record<string, string> = {
  buy_in: 'Buy-in',
  bet: 'Bet',
  payout: 'Payout',
  side_bet: 'Side bet',
  insurance: 'Insurance',
  rebuy: 'Rebuy',
  grant: 'Grant',
  kick_refund: 'Kick refund',
};

const KIND_ICONS: Record<string, string> = {
  buy_in: '+',
  rebuy: '+',
  grant: '★',
  bet: '↓',
  side_bet: '↓',
  insurance: '↓',
  payout: '↑',
  kick_refund: '↺',
};

const chips = (n: number): string => n.toLocaleString();

const pctLabel = (ratio: number | null, digits = 1): string => {
  if (ratio === null || !Number.isFinite(ratio)) return '—';
  const pct = ratio * 100;
  const abs = Math.abs(pct).toFixed(digits);
  if (pct > 0) return `+${abs}%`;
  if (pct < 0) return `−${abs}%`;
  return `${abs}%`;
};

const clockLabel = (iso: string): string => {
  const at = new Date(iso);
  const two = (n: number): string => String(n).padStart(2, '0');
  return `${two(at.getHours())}:${two(at.getMinutes())}:${two(at.getSeconds())}`;
};

const tickerOf = (name: string): string => {
  const letters = name
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 4);
  return `${letters || 'CHIP'}.CHIP`;
};

/**
 * Largest peak-to-trough drop along the balance path, as a fraction of the
 * peak at the time. Null when the series has fewer than two points.
 */
export function maxDrawdown(points: readonly { balance: number }[]): number | null {
  if (points.length < 2) return null;
  let peak = points[0]!.balance;
  let worst = 0;
  for (const p of points) {
    if (p.balance > peak) peak = p.balance;
    if (peak > 0) {
      const dd = (peak - p.balance) / peak;
      if (dd > worst) worst = dd;
    }
  }
  return worst;
}

/**
 * Normalize one series into sparkline space. X steps once per movement, not
 * by clock, so a break between hands doesn't draw as a long flat stretch.
 * Flat nights sit on 0.5.
 */
export function sparklineOf(series: BalanceSeries | null): {
  points: SparkPoint[];
  single: boolean;
} {
  if (!series || series.points.length === 0) return { points: [], single: false };
  if (series.points.length === 1) {
    return { points: [{ x: 0.5, y: 0.5 }], single: true };
  }
  const low = series.trough;
  const high = series.peak;
  const lastIndex = series.points.length - 1;
  const spanY = high - low;
  return {
    points: series.points.map((p, i) => ({
      x: i / lastIndex,
      y: spanY > 0 ? (p.balance - low) / spanY : 0.5,
    })),
    single: false,
  };
}

/**
 * The opening balance on the line's own scale, so the chart can wash the
 * stretch above it green and below it red. Null when the night is flat: a
 * water level drawn through a straight line says nothing.
 */
export function baselineOf(series: BalanceSeries | null, buyIn: number): number | null {
  if (!series || series.points.length < 2) return null;
  const span = series.peak - series.trough;
  if (span <= 0) return null;
  const frac = (buyIn - series.trough) / span;
  return Math.min(1, Math.max(0, frac));
}

/**
 * Split the sparkline into runs of one colour. A segment takes the game of
 * the movement it ends on, so the climb out of a roulette spin is roulette's
 * colour; buy-ins, rebuys and grants are the house. Each run opens on the
 * previous run's last point so the line stays joined.
 */
export function sparkRunsOf(
  series: BalanceSeries | null,
  points: readonly SparkPoint[],
): SparkRun[] {
  if (!series || points.length < 2) return [];
  const runs: SparkRun[] = [];
  for (let i = 1; i < points.length; i++) {
    const tone: SparkTone = series.points[i]!.gameType ?? 'house';
    const last = runs[runs.length - 1];
    if (last && last.tone === tone) {
      last.points.push(points[i]!);
    } else {
      runs.push({ tone, points: [points[i - 1]!, points[i]!] });
    }
  }
  return runs;
}

/** One legend key per game that drew part of the line, in first-played order. */
export function sparkLegendOf(runs: readonly SparkRun[]): SparkLegendItem[] {
  const seen = new Set<GameType>();
  for (const run of runs) {
    if (run.tone !== 'house') seen.add(run.tone);
  }
  // A night at one game needs no key — the line is simply that colour.
  if (seen.size < 2) return [];
  return [...seen].map((tone) => ({ tone, label: GAME_LABELS[tone] }));
}

/**
 * Pin the night's high and low (first time each was reached) and every house
 * move after the opening buy-in — a rebuy, a grant, a revoke — to the line.
 * A flat night has no high or low worth marking.
 */
export function sparkMarksOf(
  series: BalanceSeries | null,
  points: readonly SparkPoint[],
): SparkMark[] {
  if (!series || points.length < 2) return [];
  const marks: SparkMark[] = [];
  for (let i = 1; i < points.length; i++) {
    const p = series.points[i]!;
    if (p.gameType !== null) continue;
    const delta = p.balance - series.points[i - 1]!.balance;
    if (delta === 0) continue;
    const label = formatSignedChips(delta);
    marks.push({
      kind: delta > 0 ? 'chipsIn' : 'chipsOut',
      ...points[i]!,
      label,
      title: `${KIND_LABELS[p.kind] ?? p.kind} ${label}`,
    });
  }
  if (series.peak !== series.trough) {
    const hi = series.points.findIndex((p) => p.balance === series.peak);
    const lo = series.points.findIndex((p) => p.balance === series.trough);
    marks.push(
      { kind: 'high', ...points[hi]!, label: chips(series.peak), title: `High ${chips(series.peak)}` },
      { kind: 'low', ...points[lo]!, label: chips(series.trough), title: `Low ${chips(series.trough)}` },
    );
  }
  return marks;
}

const rateMetric = (
  label: string,
  value: number | null,
  opts: { asPct?: boolean; signed?: boolean } = {},
): MarketMetric => {
  const { asPct = true, signed = false } = opts;
  return {
    label,
    value: asPct
      ? pctLabel(value)
      : value === null
        ? '—'
        : formatSignedChips(Math.round(value)),
    tone: !signed || value === null || value === 0 ? null : value > 0 ? 'win' : 'loss',
  };
};

const nightMetrics = (totals: MemberStatsView): MarketMetric[] => {
  const d = deriveStats({ ...EMPTY_STATS, ...totals });
  const out: MarketMetric[] = [
    rateMetric('ROI', d.roi, { signed: true }),
    rateMetric('Win rate', d.winRate),
    {
      label: 'Avg bet',
      value: d.avgBet === null ? '—' : chips(Math.round(d.avgBet)),
      tone: null,
    },
    rateMetric('Avg / rnd', d.avgResult, { asPct: false, signed: true }),
  ];
  if (totals.bestWinStreak > 1) {
    out.push({ label: 'Best run', value: `${totals.bestWinStreak}W`, tone: 'win' });
  }
  if (totals.winStreak > 0) {
    out.push({
      label: 'Streak',
      value: `${totals.winStreak}W`,
      tone: 'win',
    });
  } else if (totals.lossStreak > 0) {
    out.push({
      label: 'Streak',
      value: `${totals.lossStreak}L`,
      tone: 'loss',
    });
  }
  if (d.roundsPerHour !== null) {
    out.push({
      label: 'Pace',
      value: `${Math.round(d.roundsPerHour)}/hr`,
      tone: null,
    });
  }
  return out;
};

const toneOf = (n: number): 'win' | 'loss' | 'even' => (n > 0 ? 'win' : n < 0 ? 'loss' : 'even');

/**
 * Fold the newest-first tape into hands: consecutive rows sharing a round
 * collapse into one group, named by the game when that round is on the
 * loaded trades page. House moves stay one row each.
 */
export function ledgerTapeModel(
  entries: readonly LedgerTapeEntry[],
  hasMore = false,
  rounds: readonly RoundEntry[] = [],
): { groups: LedgerGroupVM[]; hasMore: boolean } {
  const gameOf = new Map(rounds.map((r) => [r.roundId, r.gameType]));
  // House moves draw no meter, so a buy-in never flattens every hand's bars.
  const biggest = Math.max(
    1,
    ...entries.filter((e) => e.roundId !== null).map((e) => Math.abs(e.amount)),
  );

  const runs: LedgerTapeEntry[][] = [];
  for (const entry of entries) {
    const last = runs[runs.length - 1];
    if (last && entry.roundId !== null && last[0]!.roundId === entry.roundId) {
      last.push(entry);
    } else {
      runs.push([entry]);
    }
  }

  const groups = runs.map((run): LedgerGroupVM => {
    const newest = run[0]!;
    const oldest = run[run.length - 1]!;
    const net = run.reduce((sum, e) => sum + e.amount, 0);
    const house = newest.roundId === null;
    const game = house ? undefined : gameOf.get(newest.roundId!);
    const kindLabel = KIND_LABELS[newest.kind] ?? newest.kind;
    return {
      key: newest.id,
      title: house ? kindLabel : game ? GAME_LABELS[game] : 'Round',
      glyph: house ? (KIND_ICONS[newest.kind] ?? '•') : game ? GAME_GLYPHS[game] : '▪',
      whenLabel: clockLabel(oldest.at),
      netLabel: formatSignedChips(net),
      tone: toneOf(net),
      balanceLabel: chips(newest.balanceAfter),
      house,
      lines: [...run].reverse().map((entry) => ({
        key: entry.id,
        whenLabel: clockLabel(entry.at),
        kindLabel: KIND_LABELS[entry.kind] ?? entry.kind,
        icon: KIND_ICONS[entry.kind] ?? '•',
        amountLabel: formatSignedChips(entry.amount),
        balanceLabel: chips(entry.balanceAfter),
        tone: toneOf(entry.amount),
        barFrac: Math.abs(entry.amount) / biggest,
      })),
    };
  });
  return { groups, hasMore };
}

export interface MemberMarketInput {
  /**
   * The membership card the desk quotes. A leaderboard row is enough — the
   * phone also passes history/ledger for the tapes; the TV board does not.
   */
  me: Pick<CasinoMemberView, 'memberId' | 'name' | 'chips' | 'buyIn'> &
    Partial<Pick<CasinoMemberView, 'totals'>> | null;
  /** This member's line from the public timeline, or null while loading. */
  series: BalanceSeries | null;
  rounds: readonly RoundEntry[];
  roundsHasMore?: boolean;
  ledger: readonly LedgerTapeEntry[];
  ledgerHasMore?: boolean;
  /** The game this member played most in the quoted window, when known. */
  favoriteGame?: GameType | null;
}

/**
 * Fold the membership card, the personal balance line and both audit tapes
 * into one desk. A null membership is the newcomer state — no quote yet.
 */
export function memberMarketModel(input: MemberMarketInput): MemberMarketVM {
  const {
    me,
    series,
    rounds,
    roundsHasMore = false,
    ledger,
    ledgerHasMore = false,
    favoriteGame = null,
  } = input;

  if (!me) {
    return {
      ready: false,
      symbol: '—',
      name: '',
      lastLabel: '—',
      changeLabel: '—',
      changePctLabel: '—',
      changeTone: 'even',
      highLabel: '—',
      lowLabel: '—',
      volumeLabel: '—',
      drawdownLabel: '—',
      metrics: [],
      highlights: [],
      spark: [],
      sparkRuns: [],
      sparkLegend: [],
      sparkMarks: [],
      sparkSingle: false,
      sparkBaseline: null,
      trades: memberHistoryModel([], false),
      ledgerGroups: [],
      ledgerHasMore: false,
      emptyLine: null,
    };
  }

  const change = me.chips - me.buyIn;
  const changePct = me.buyIn > 0 ? change / me.buyIn : null;
  const changeTone: MemberMarketVM['changeTone'] =
    change > 0 ? 'win' : change < 0 ? 'loss' : 'even';
  const spark = sparklineOf(series);
  const sparkRuns = sparkRunsOf(series, spark.points);
  const dd = series ? maxDrawdown(series.points) : null;
  const tape = ledgerTapeModel(ledger, ledgerHasMore, rounds);
  const hasMoved = (series?.points.length ?? 0) > 0 || rounds.length > 0 || ledger.length > 0;

  return {
    ready: true,
    symbol: tickerOf(me.name),
    name: me.name,
    lastLabel: chips(me.chips),
    changeLabel: formatSignedChips(change),
    changePctLabel: pctLabel(changePct),
    changeTone,
    highLabel: series ? chips(series.peak) : chips(me.chips),
    lowLabel: series ? chips(series.trough) : chips(me.buyIn),
    volumeLabel: chips(me.totals?.wagered ?? 0),
    drawdownLabel: dd === null || dd === 0 ? '—' : pctLabel(-dd),
    metrics: nightMetrics(me.totals ?? EMPTY_STATS),
    highlights: highlightsOf(me.totals ?? EMPTY_STATS, favoriteGame),
    spark: spark.points,
    sparkRuns,
    sparkLegend: sparkLegendOf(sparkRuns),
    sparkMarks: sparkMarksOf(series, spark.points),
    sparkSingle: spark.single,
    sparkBaseline: baselineOf(series, me.buyIn),
    trades: memberHistoryModel(rounds, roundsHasMore),
    ledgerGroups: tape.groups,
    ledgerHasMore: tape.hasMore,
    emptyLine: hasMoved ? null : 'No prints yet — sit down and the tape starts.',
  };
}

/**
 * The TV's public board: one chip desk per leaderboard row, quoted off the
 * public totals and that member's timeline line. No trade blotter — dealt
 * cards stay behind a credential.
 */
export function boardDeskModel(
  entry: CasinoLeaderboardEntry,
  series: BalanceSeries | null,
): MemberMarketVM {
  return memberMarketModel({
    me: {
      memberId: entry.memberId,
      name: entry.name,
      chips: entry.chips,
      buyIn: entry.buyIn,
      totals: entry.totals,
    },
    series,
    rounds: [],
    ledger: [],
    favoriteGame: entry.favoriteGame,
  });
}
